/**
 * @file TabHistory.jsx
 * @description TabHistory component for displaying and managing tab history.
 *              Each row is a single message event: who sent it (Customer / Cara),
 *              the message text, and when it happened. Rows are ordered strictly
 *              newest-first, and the platform-inserted "Hi" opener is hidden.
 * @component TabHistory
 * @exports TabHistory - Main component for displaying and managing tab history.
 */

import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import { getcustomerhistory } from "../../../services/customerinfoModule/historyServices";
import { useRegisterCsvExport } from "../../customerModule/customers/CsvExportContext.jsx";
import { HISTORY_COLUMNS } from "../../customerModule/customers/customerExportConfig.js";

const timeMs = (val) => {
  if (!val) return 0;
  const t = new Date(val).getTime();
  return Number.isNaN(t) ? 0 : t;
};

const USER_COLOR = "#378ADD";
const CARA_COLOR = "#1D9E75";

const isCustomerSender = (who) => /^(customer|user)$/i.test(String(who || "").trim());
const isCaraSender     = (who) => /^cara$/i.test(String(who || "").trim());

// The feed labels the customer "Customer"; the transcript in the drill-down
// popup and the Texts tab both say USER, so normalise to match.
const senderLabel = (who) => {
  if (isCustomerSender(who)) return "USER";
  if (isCaraSender(who))     return "CARA";
  return String(who || "—").toUpperCase();
};

const senderColor = (who) => {
  if (isCustomerSender(who)) return USER_COLOR;
  if (isCaraSender(who))     return CARA_COLOR;
  return "text.primary";
};

// The opening "Hi" is written by the platform, not typed by the customer, so
// it's noise in a transcript. This feed carries no iniconv flag, so the match
// is on text alone — a customer who genuinely replies "Hi" is indistinguishable
// here. Add iniconv to the getcustomerhistory SELECT and gate on it if that
// turns out to matter.
const isInsertedHi = (row) =>
  isCustomerSender(row.Who) && /^hi[.!]?$/i.test(String(row.message || "").trim());

const CustomerHistory = ({ customerguid }) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);

  // Newest first. On an exact timestamp tie Cara sits above the message she's
  // answering, then original order breaks the remainder so the sort is stable.
  const rows = useMemo(
    () =>
      (history || [])
        .map((row, _idx) => ({ row, _idx }))
        .filter(({ row }) => !isInsertedHi(row))
        .sort((a, b) => {
          const d = timeMs(b.row.event_time) - timeMs(a.row.event_time);
          if (d) return d;
          if (isCaraSender(a.row.Who) !== isCaraSender(b.row.Who)) {
            return isCaraSender(a.row.Who) ? -1 : 1;
          }
          return b._idx - a._idx;
        })
        .map(({ row }) => row),
    [history]
  );

  useRegisterCsvExport({
    name: "history",
    columns: HISTORY_COLUMNS,
    rows,
    enabled: !loading,
  });

  useEffect(() => {
    if (customerguid) fetchHistory();
  }, [customerguid]);

  const fetchHistory = async () => {
    setLoading(true);
    try {
      const data = await getcustomerhistory({ customerguid });
      setHistory(data || []);
    } catch (error) {
      console.error("Error fetching history:", error);
    } finally {
      setLoading(false);
    }
  };

  // MM/dd/yyyy hh:mm:ss AM/PM
  const formatDate = (isoDate) => {
    if (!isoDate) return "—";
    const date = new Date(isoDate);
    if (Number.isNaN(date.getTime())) return isoDate;
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const dd = String(date.getDate()).padStart(2, "0");
    const yyyy = date.getFullYear();
    let hours = date.getHours();
    const mins = String(date.getMinutes()).padStart(2, "0");
    const secs = String(date.getSeconds()).padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12 || 12;
    return `${mm}/${dd}/${yyyy} ${String(hours).padStart(2, "0")}:${mins}:${secs} ${ampm}`;
  };

  return (
    <Box sx={{ p: 2 }}>
      <TableContainer
        component={Paper}
        sx={{
          maxHeight: 500,
          overflowY: "auto",
        }}
      >
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 210, whiteSpace: "nowrap" }}><b>Date</b></TableCell>
              <TableCell sx={{ width: 80 }}><b>Channel</b></TableCell>
              <TableCell sx={{ width: 80 }}><b>Sender</b></TableCell>
              <TableCell><b>Message</b></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 && !loading ? (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  No history found
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row, index) => (
                <TableRow key={index}>
                  <TableCell sx={{
                    whiteSpace: "nowrap",
                    fontVariantNumeric: "tabular-nums",
                    verticalAlign: "top",
                    color: "text.secondary",
                  }}>
                    {formatDate(row.event_time)}
                  </TableCell>
                  <TableCell sx={{ verticalAlign: "top", color: "text.secondary" }}>
                    {row.Channel}
                  </TableCell>
                  <TableCell sx={{
                    verticalAlign: "top",
                    color: senderColor(row.Who),
                    fontWeight: 600,
                    fontSize: "0.72rem",
                    letterSpacing: "0.04em",
                  }}>
                    {senderLabel(row.Who)}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: "pre-wrap", verticalAlign: "top" }}>
                    {row.message || "—"}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default CustomerHistory;